<template xmlns:v="http://www.w3.org/1999/XSL/Transform">
    <templateItem
        v-bind:metadata="metadata"
        v-bind:isSelected="isSelected"
        v-bind:parentId="parentId"
        v-bind:isParent="isParent"
        v-bind:hideSort="true"
        v-bind:controlsClass="'top'"
        v-bind:classes="classes"
        v-bind:styles="style"
        v-on:delete-item="$emit('delete-column', $event)"
        v-on:select-item="$emit('select-column', $event)"
        v-on:lock-item="$emit('lock-column', $event)"
        v-bind:locked="locked"
        ref="column"
    >

        <template v:slot="settings" v-if="showSettings">
            <template-column-settings 
                v-model="model"
            />

            <template-column-button-settings
                    v-if="isButton"
                    v-model="contentSettingsModel"
            />

            <template-column-text-settings
                    v-else-if="isText"
                    v-model="contentSettingsModel"
                    v-bind:column-rect="columnRect"
            />

            <template-column-image-settings
                    v-else-if="isImage"
                    v-model="contentSettingsModel"
            />
        </template>

     
        <template-column-button-content
                v-if="isButton"
                v-bind:metadata="contentSettingsModel"
                @text-changed="contentSettingsModel.text = $event"
        >
        </template-column-button-content>

        <template-column-text-content
                v-else-if="isText"
                v-bind:metadata="contentSettingsModel"
                @text-changed="contentSettingsModel.text = $event"
        >
        </template-column-text-content>

        <template-column-image-content
            v-else-if="isImage"
            v-bind:metadata="contentSettingsModel"
        >
        </template-column-image-content>


        <slot></slot>
    </templateItem>
</template>

<script>
    import imageUrlMixin from '../Mixins/image-url';
    
    import columnTypes from "../column-content-types";
    import {
        applyDefaults,
        
        columnDefaults,
        buttonDefaults, 
        textDefaults, 
        imageDefaults
    } from "../defaults";
    
    import TemplateItem from './TemplateItem.vue';

    import TemplateColumnSettings from './Settings/TemplateColumnSettings.vue';
    
    import TemplateColumnButtonContent from './ColumnContent/TemplateColumnButton.vue';
    import TemplateColumnButtonSettings from './Settings/TemplateColumnButtonSettings.vue';

    import TemplateColumnTextContent from './ColumnContent/TemplateColumnText.vue';
    import TemplateColumnTextSettings from './Settings/TemplateColumnTextSettings.vue';
    
    import TemplateColumnImageContent from './ColumnContent/TemplateColumnImage.vue';
    import TemplateColumnImageSettings from './Settings/TemplateColumnImageSettings.vue';

    import RenamableProperty from '../../../Components/Util/RenamableProperty.vue';;

    const defaultContentSettingsPerType = {};

    defaultContentSettingsPerType[columnTypes.button] = buttonDefaults;
    defaultContentSettingsPerType[columnTypes.image] = imageDefaults;
    defaultContentSettingsPerType[columnTypes.text] = textDefaults;
    
    export default {
        name: 'template-column',
        mixins: [imageUrlMixin],
        components: {
            RenamableProperty,
            'TemplateItem': TemplateItem,

            TemplateColumnSettings,
            
            TemplateColumnButtonContent,
            TemplateColumnButtonSettings,
            
            TemplateColumnTextContent,
            TemplateColumnTextSettings,
            
            TemplateColumnImageContent,
            TemplateColumnImageSettings
        },
        created(){
            const vm = this;
            
            vm.metadata.contentSettings = vm.metadata.contentSettings || {};
            
            //Start off by enriching our metadata
            vm.copyDefaultsTo(defaultContentSettingsPerType[vm.columnTypeName], vm.metadata.contentSettings);
            
            //Then make it our new model
            vm.model = vm.metadata;
        },
        mounted(){
            const vm = this;
            
            //Our column (and thus it's width) becomes available only after mount
            vm.columnRect = vm.$refs.column.$el.getBoundingClientRect();
        },
        methods: {
            copyDefaultsTo(defaults, target){
                return applyDefaults(defaults, target);
            },
            emitSelectItem() {
                this.$refs.column.selectItem();
            }
        },
        computed: {
            showSettings() {
                return this.isSelected && !this.lockedByParent;
            },
            lockedByParent() {
                return this.locked && this.isParent;
            },
            columnType() {
                const vm = this;
                
                return vm.columnTypes.find((type) => { 
                    return type.Id === vm.columnTypeId 
                });
            },
            contentSettingsModel: {
                get(){
                    const vm = this;
                    return vm.metadata.contentSettings;
                },
                set(value){
                    const vm = this;
                    vm.metadata.contentSettings = value;
                }
            },
            columnTypeName(){
                return this.columnType.Value.name;
            },
            isButton() {
                return this.columnTypeName === columnTypes.button;
            },
            isImage(){
                return this.columnTypeName === columnTypes.image;
            },
            isText() {
                return this.columnTypeName === columnTypes.text;
            },
            style() {
                const vm = this;
                
                const {
                    mediaId,
                    opacity, 
                    backgroundColor,
                    backgroundSize
                } = vm.model.style;
                
                const rules = [
                    `background-color: ${(backgroundColor != '' ? backgroundColor : this.parentColor)}`,
                    `opacity: ${(100 - opacity) / 100}`
                ];

                //Background image is optional
                if(mediaId){
                    rules.push(`background-image: url(${vm.getImageUrl(mediaId, vm.model.style)})`);
                    rules.push(`background-size: ${vm.model.style.backgroundSize || 'cover' }`);
                }
                
                return rules.join(';');
            },
            isParent() {
                return this.currentTemplateId.toLowerCase() !== this.templateId.toLowerCase();
            },
            classes() {
                const classList = [
                    'mx-auto',
                    'template-column'
                ];
                
                if(this.isSelected){
                    classList.push('selected-column');
                }
                
                return classList.join(' ');
            }
        },
        data() {
            return {
                model: {},
                columnRect: {
                    x: 0,
                    y: 0,
                    width: 0,
                    height: 0
                }
            }
        },
        watch: {
            model: {
                handler(value) {
                    const vm = this;

                    //Include contentsettings as not to reset our column content.
                    value.contentSettings = vm.metadata.contentSettings;
                    vm.$emit('metadata-changed', value);
                },
                deep: true
            }
        },
        props: {
            id: {
                type: String,
                required: false
            },
            parentId: {
                type: String,
                default: null
            },
            locked: {
                type: Boolean,
                default: false
            },
            lockStateChanged: {
                type: Boolean,
                required: true
            },
            isSelected: {
                type: Boolean,
                required: true
            },
            templateId: {
                type: String,
                required: true
            },
            currentTemplateId: {
                type: String,
                required: true
            },
            rowId: {
                type: String,
                required: false
            },
            columnTypes: {
                type: Array,
                required: true
            },
            columnTypeId: {
                type: String,
                required: true
            },
            metadata: {
                type: Object,
                required: true,
                default: function () {
                    return { index: 0 }
                }
            },
            parentColor: {
                type: String,
                default: function () {
                    return '#ffffff';
                }
            }
        }
    };
</script>

<style scoped src="../Style/template-column.scss" lang="scss">
</style>
